import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { useTranslation } from "react-i18next";
import MarketQualityAttributeAssignmentEditConfirmationModal from "./marketQualityAttributeAssignmentEditConfirmationModal";
import {
  endQualityAttributeValueMarketAssignment,
  updateQualityAttributeValueMarketAssignment,
} from "../../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

const MarketQualityAttributeAssignmentsTable = ({
  marketId,
  assignments: propAssignments,
  allowEdit,
  onAssignmentUpdated,
  showActiveOnly = true,
}) => {
  const { t } = useTranslation(["markets"]);
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [showDateEditModal, setShowDateEditModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [oldValue, setOldValue] = useState(null);
  const [newValue, setNewValue] = useState(null);
  const [endingAssignment, setEndingAssignment] = useState(null);
  const [pendingCellChange, setPendingCellChange] = useState(null);
  const isRevertingRef = useRef(false);

  // Map API data to view model
  const mapToViewModel = useCallback((assignments) => {
    return assignments.map((assignment) => ({
      ...assignment,
      qualityAttributeValue: assignment.qualityAttributeValue || "",
      startDate: assignment.startDate
        ? new Date(assignment.startDate).toISOString().split("T")[0]
        : "",
      endDate: assignment.endDate
        ? new Date(assignment.endDate).toISOString().split("T")[0]
        : null,
    }));
  }, []);

  // Fetch assignments on component mount if not provided as props
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        if (!propAssignments || propAssignments.length === 0) {
          // If no props, we'd need to fetch, but for now we'll use props
          setAssignments([]);
        } else {
          setAssignments(mapToViewModel(propAssignments));
        }
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssignments();
  }, [marketId, propAssignments, mapToViewModel]);

  // Handle start date cell value changed (native AG Grid editing)
  const handleStartDateChange = useCallback(
    (params) => {
      // Don't open modal if we're reverting a cancelled edit
      if (isRevertingRef.current) {
        return;
      }

      const oldValue = params.oldValue || "";
      const newValue = params.newValue || "";
      const data = params.data;

      // Validate: Start Date cannot be empty/Not Set
      if (!newValue || newValue.trim() === "") {
        // Revert the change
        params.node.setDataValue(params.column.colId, oldValue);
        toast.error(t("StartDateRequired") || "Start Date is required");
        return;
      }

      // Validate: end date must be >= start date if end date exists
      if (data.endDate && newValue && data.endDate < newValue) {
        // Revert the change
        params.node.setDataValue(params.column.colId, oldValue);
        toast.error(t("StartDateMustBeBeforeEndDate"));
        return;
      }

      // Store old value and AG Grid params for potential revert
      setEditingAssignment(data);
      setEditingField("startDate");
      setOldValue(oldValue);
      setNewValue(newValue);
      setPendingCellChange(params);
      setShowDateEditModal(true);
    },
    [t]
  );

  // Handle end date cell value changed (native AG Grid editing)
  const handleEndDateChange = useCallback(
    (params) => {
      // Don't open modal if we're reverting a cancelled edit
      if (isRevertingRef.current) {
        return;
      }

      const oldValue = params.oldValue || null;
      const newValue = params.newValue || null;
      const data = params.data;

      // Validate: end date must be >= start date if both exist
      if (data.startDate && newValue && newValue < data.startDate) {
        // Revert the change
        params.node.setDataValue(params.column.colId, oldValue);
        toast.error(t("StartDateMustBeBeforeEndDate"));
        return;
      }

      // Store old value and AG Grid params for potential revert
      setEditingAssignment(data);
      setEditingField("endDate");
      setOldValue(oldValue);
      setNewValue(newValue);
      setPendingCellChange(params);
      setShowDateEditModal(true);
    },
    [t]
  );

  // Handle End button click
  const handleEndClick = useCallback((assignment) => {
    setEndingAssignment(assignment);
    setShowEndModal(true);
  }, []);

  // Confirm date edit
  const handleConfirmDateEdit = useCallback(async () => {
    if (!editingAssignment || !editingField || !pendingCellChange) return;

    try {
      // Prepare assignment for API update
      // Convert date strings (YYYY-MM-DD) to ISO date strings for API
      const startDateValue =
        editingField === "startDate" ? newValue : editingAssignment.startDate;
      const endDateValue =
        editingField === "endDate"
          ? newValue || null
          : editingAssignment.endDate;

      const updatedAssignment = {
        qualityAttributeValueId: editingAssignment.qualityAttributeValueId,
        marketId: editingAssignment.marketId,
        startDate: startDateValue
          ? new Date(startDateValue + "T00:00:00Z").toISOString()
          : null,
        endDate: endDateValue
          ? new Date(endDateValue + "T00:00:00Z").toISOString()
          : null,
        eventId: uuid(),
      };

      await updateQualityAttributeValueMarketAssignment(updatedAssignment);

      // Update local state (cell value already updated by AG Grid)
      const updatedAssignments = assignments.map((a) =>
        a.qualityAttributeValueId ===
          editingAssignment.qualityAttributeValueId &&
        a.marketId === editingAssignment.marketId
          ? { ...a, [editingField]: newValue }
          : a
      );
      setAssignments(updatedAssignments);

      // Reset state
      setShowDateEditModal(false);
      setEditingAssignment(null);
      setEditingField(null);
      setOldValue(null);
      setNewValue(null);
      setPendingCellChange(null);

      if (onAssignmentUpdated) {
        onAssignmentUpdated();
      }

      toast.success(t("AssignmentUpdated"));
    } catch (error) {
      console.error("Error updating assignment:", error);

      // Revert cell value on error
      if (pendingCellChange) {
        pendingCellChange.node.setDataValue(
          pendingCellChange.column.colId,
          oldValue
        );
      }

      toast.error(
        t("ErrorSaving") + " " + (error.response?.data || error.message)
      );
    }
  }, [
    editingAssignment,
    editingField,
    newValue,
    oldValue,
    assignments,
    onAssignmentUpdated,
    pendingCellChange,
    t,
  ]);

  // Cancel date edit - revert cell value
  const handleCancelDateEdit = useCallback(() => {
    // Set flag to prevent onCellValueChanged from reopening modal
    isRevertingRef.current = true;

    // Reset state
    const cellChangeToRevert = pendingCellChange;
    const valueToRevert = oldValue;

    setShowDateEditModal(false);
    setEditingAssignment(null);
    setEditingField(null);
    setOldValue(null);
    setNewValue(null);
    setPendingCellChange(null);

    // Revert cell value using AG Grid API
    // The isRevertingRef flag prevents onCellValueChanged from reopening modal
    if (cellChangeToRevert) {
      cellChangeToRevert.node.setDataValue(
        cellChangeToRevert.column.colId,
        valueToRevert
      );
    }

    // Reset flag after a short delay to allow the revert to complete
    setTimeout(() => {
      isRevertingRef.current = false;
    }, 100);
  }, [pendingCellChange, oldValue]);

  // Confirm end subscription
  const handleConfirmEnd = useCallback(async () => {
    if (!endingAssignment) return;

    try {
      await endQualityAttributeValueMarketAssignment(
        endingAssignment.marketId,
        endingAssignment.qualityAttributeValueId,
        uuid()
      );

      // Update local state - set endDate to today
      const today = new Date().toISOString().split("T")[0];
      const updatedAssignments = assignments.map((a) =>
        a.qualityAttributeValueId ===
          endingAssignment.qualityAttributeValueId &&
        a.marketId === endingAssignment.marketId
          ? { ...a, endDate: today }
          : a
      );
      setAssignments(updatedAssignments);

      setShowEndModal(false);
      setEndingAssignment(null);

      if (onAssignmentUpdated) {
        onAssignmentUpdated();
      }

      toast.success(t("AssignmentEnded"));
    } catch (error) {
      console.error("Error ending assignment:", error);
      toast.error(
        t("ErrorSaving") + " " + (error.response?.data || error.message)
      );
    }
  }, [endingAssignment, assignments, onAssignmentUpdated, t]);

  // End button renderer - AG Grid passes params directly
  const endButtonRenderer = useCallback(
    (params) => {
      const data = params.data;

      if (!allowEdit) {
        return null;
      }

      // Don't show End button if assignment is not active (EndDate is not null and EndDate < today)
      if (data?.endDate) {
        const endDate = new Date(data.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        if (endDate < today) {
          return null;
        }
      }

      return (
        <button
          type="button"
          className="btn btn-sm btn-delete"
          onClick={(e) => {
            e.stopPropagation();
            handleEndClick(data);
          }}
        >
          {t("End")}
        </button>
      );
    },
    [allowEdit, handleEndClick, t]
  );

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "qualityAttributeValue",
        headerName: t("QualityAttributeValue"),
        sortable: true,
        sort: "asc",
        filter: true,
        flex: 2,
      },
      {
        field: "startDate",
        headerName: t("StartDate"),
        sortable: true,
        filter: true,
        flex: 1.5,
        editable: (params) => allowEdit,
        cellEditor: "agDateStringCellEditor",
        cellEditorParams: {
          min: "2000-01-01",
          max: "2099-12-31",
        },
        valueFormatter: (params) => params.value || "",
        onCellValueChanged: handleStartDateChange,
        cellClass: (params) => (allowEdit ? "clickable-transparent" : ""),
      },
      {
        field: "endDate",
        headerName: t("EndDate"),
        sortable: true,
        filter: true,
        flex: 1.5,
        editable: (params) => allowEdit,
        cellEditor: "agDateStringCellEditor",
        cellEditorParams: {
          min: (params) => params.data.startDate || undefined,
          max: "2099-12-31",
        },
        valueFormatter: (params) => params.value || "",
        onCellValueChanged: handleEndDateChange,
        cellClass: (params) => (allowEdit ? "clickable-transparent" : ""),
      },
      {
        headerName: t("Actions"),
        sortable: false,
        filter: false,
        width: 100,
        cellRenderer: endButtonRenderer,
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [
      t,
      allowEdit,
      handleStartDateChange,
      handleEndDateChange,
      endButtonRenderer,
    ]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  // Filter assignments based on active filter
  const filteredAssignments = useMemo(() => {
    if (!showActiveOnly) return assignments;
    const today = new Date().toISOString().split("T")[0];
    return assignments.filter((a) => !a.endDate || a.endDate >= today);
  }, [assignments, showActiveOnly]);

  if (isLoading) return <Loading />;

  return (
    <>
      <ThemeableAgGrid
        className="table-bold"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={filteredAssignments}
        gridOptions={{
          suppressCellFocus: false,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
        }}
        getRowId={(params) =>
          `${params.data.marketId}-${params.data.qualityAttributeValueId}`
        }
        animateRows={true}
        domLayout="autoHeight"
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={[5, 10, 20, 50, 100]}
        onPaginationChanged={(event) => {
          const newPageSize = event.api.paginationGetPageSize();
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
        }}
        suppressPaginationPanel={false}
      />

      {/* Date Edit Confirmation Modal */}
      {showDateEditModal && editingAssignment && (
        <MarketQualityAttributeAssignmentEditConfirmationModal
          show={showDateEditModal}
          onHide={handleCancelDateEdit}
          onConfirm={handleConfirmDateEdit}
          field={editingField}
          oldValue={oldValue}
          newValue={newValue}
          assignment={editingAssignment}
        />
      )}

      {/* End Confirmation Modal */}
      <Modal show={showEndModal} onHide={() => setShowEndModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("EndAssignment")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t("ConfirmEndAssignment")} "
            <b>{endingAssignment?.qualityAttributeValue}</b>" effective today,{" "}
            <b>
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </b>
            ?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEndModal(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={handleConfirmEnd}>
            {t("End")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MarketQualityAttributeAssignmentsTable;

